import { Fade, Paper, Stack } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import ChatBotAssistantSelection from 'pages/chatbox/components/chatBotAssistantSelection';
import ChatBoxWindow, { SenderEnum } from 'pages/chatbox/components/chatBoxWindow';
import MDBox from 'components/MDBox';
import { useGenerateStreamMutation } from 'services/assistantApi';
import ChatBoxInputArea from 'pages/chatbox/components/chatBoxInputArea';
import { useNotification } from 'hook/useNotification';
import { generateUUID } from 'utils/cryptoUtils';

export default function ChatBox({ assistant, isPublic = false, isEmbedded = false }) {
  const { formatMessage: msg } = useIntl();
  const { addNotification } = useNotification();

  const [messages, setMessages] = useState([]);
  const [messageLoading, setMessageLoading] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [globalDragInProgress, setGlobalDragInProgress] = useState(false);

  const controllerRef = useRef(null);
  const simulateStreamIntervalRef = useRef(null);

  const [generateStream] = useGenerateStreamMutation();

  const handleMessageReceived = useCallback((newChunk) => {
    if (newChunk != null && newChunk !== 'null') {
      setMessages((prevMessages) => {
        const lastMessage = prevMessages[prevMessages.length - 1];
        if (lastMessage && lastMessage.sender === SenderEnum.AI) {
          return [
            ...prevMessages.slice(0, -1),
            {
              id: generateUUID(),
              sender: SenderEnum.AI,
              text: lastMessage.text + newChunk
            }
          ];
        }
        return [...prevMessages, { id: generateUUID(), sender: SenderEnum.AI, text: newChunk }];
      });
    } else {
      setMessageLoading(false);
    }
  }, []);

  const simulateStreamedMessage = useCallback(
    (message) => {
      const timeout = 3;
      let index = 0;
      const chunkSize = 1;
      clearInterval(simulateStreamIntervalRef.current);
      simulateStreamIntervalRef.current = setInterval(() => {
        if (index < message.length) {
          const chunk = message.slice(index, index + chunkSize);
          handleMessageReceived(chunk);
          index += chunkSize;
        } else {
          setMessageLoading(false);
          clearInterval(simulateStreamIntervalRef.current);
        }
      }, timeout);
    },
    [handleMessageReceived]
  );

  useEffect(() => {
    if (selectedAssistant && selectedAssistant.welcoming) {
      setMessageLoading(true);
      simulateStreamedMessage(selectedAssistant.welcoming);
    }
  }, [selectedAssistant, simulateStreamedMessage]);

  const handleMessageSend = useCallback(
    async (files, request) => {
      setMessageLoading(true);
      const controller = new AbortController();
      controllerRef.current = controller;
      try {
        await generateStream({
          files,
          request,
          signal: controller.signal,
          onChunk: (chunk) => {
            handleMessageReceived(chunk);
          },
          isPublic
        }).unwrap();
      } catch {
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.chatbox.error.stream.message' })
        });
        setMessageLoading(false);
      }
    },
    [addNotification, generateStream, handleMessageReceived, msg, isPublic]
  );

  const handleCancel = useCallback(() => {
    // Cancel the stream from digi-ai (stop action)
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
    // Cancel the stream from welcoming (change assistant)
    if (simulateStreamIntervalRef.current) {
      clearInterval(simulateStreamIntervalRef.current);
      simulateStreamIntervalRef.current = null;
    }
    setMessageLoading(false);
  }, []);

  const handleAssistantChange = useCallback(
    (newAssistant) => {
      handleCancel();
      setMessages([]);
      setSelectedAssistant(newAssistant);
    },
    [handleCancel]
  );

  useEffect(() => {
    handleAssistantChange(assistant);
  }, [handleAssistantChange, assistant]);

  // DRAG/DROP GLOBAL ---
  useEffect(() => {
    let dragCounter = 0;

    const handleDragEnter = (e) => {
      dragCounter++;
      if (dragCounter === 1 && e.dataTransfer.types.includes('Files')) {
        setGlobalDragInProgress(true);
      }
    };

    const handleDragLeave = () => {
      dragCounter--;
      if (dragCounter === 0) {
        setGlobalDragInProgress(false);
      }
    };

    const handleDropGlobal = () => {
      dragCounter = 0;
      setGlobalDragInProgress(false);
    };

    const handleDragEndGlobal = () => {
      dragCounter = 0;
      setGlobalDragInProgress(false);
    };

    // Attacher les écouteurs sur l'élément document.documentElement (toute la page)
    document.documentElement.addEventListener('dragenter', handleDragEnter);
    document.documentElement.addEventListener('dragleave', handleDragLeave);
    document.documentElement.addEventListener('drop', handleDropGlobal);
    document.documentElement.addEventListener('dragend', handleDragEndGlobal);

    return () => {
      // Nettoyer les écouteurs lors du démontage du composant
      document.documentElement.removeEventListener('dragenter', handleDragEnter);
      document.documentElement.removeEventListener('dragleave', handleDragLeave);
      document.documentElement.removeEventListener('drop', handleDropGlobal);
      document.documentElement.removeEventListener('dragend', handleDragEndGlobal);
    };
  }, []);
  // --- DRAG/DROP GLOBAL ---

  return (
    <Fade in timeout={(1000, 500)}>
      <Paper
        elevation={assistant == null ? 2 : 0}
        sx={{
          flex: 1,
          borderRadius: '10px',
          p: 2,
          mb: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Stack
          direction="column"
          spacing={2}
          flex={1}
          id="chatbotStack"
          sx={{ overflow: 'hidden' }}
        >
          {assistant == null ? (
            <MDBox
              id="assistantSelectionContainer"
              display="flex"
              sx={{
                justifyContent: 'center',
                width: '100%',
                maxWidth: '500px',
                alignSelf: 'center',
                marginTop: '0.5rem !important'
              }}
            >
              <ChatBotAssistantSelection
                selectedAssistant={selectedAssistant}
                onAssistantChange={handleAssistantChange}
                isPublic={isPublic}
              />
            </MDBox>
          ) : null}
          <MDBox
            id="windowContainer"
            display="flex"
            flex={1}
            sx={{ justifyContent: 'center', overflow: 'auto' }}
          >
            <ChatBoxWindow messages={messages} />
          </MDBox>
          <MDBox
            id="inputAreaContainer"
            display="flex"
            sx={{ justifyContent: 'center', width: '100%', maxWidth: '100%' }}
          >
            <ChatBoxInputArea
              setMessages={setMessages}
              onMessageSend={handleMessageSend}
              selectedAssistant={selectedAssistant}
              messageLoading={messageLoading}
              onCancel={handleCancel}
              isEmbedded={isEmbedded || isPublic}
              dragInProgress={globalDragInProgress}
            />
          </MDBox>
        </Stack>
      </Paper>
    </Fade>
  );
}

ChatBox.propTypes = {
  assistant: PropTypes.shape({
    id: PropTypes.number,
    label: PropTypes.string,
    icon: PropTypes.string,
    code: PropTypes.string.isRequired,
    restrictions: PropTypes.shape({
      maxFilesCount: PropTypes.number,
      allowedExtensions: PropTypes.array
    })
  }),
  isPublic: PropTypes.bool,
  isEmbedded: PropTypes.bool
};
